/**
 * Sending a requester to sign a draft request (`awaiting_signature`) on the
 * signing page.
 *
 * `useSignRequest` is the one implementation: mint the challenge, then open
 * the signing page, which returns to My Access once signed (My Access's Sign
 * buttons). `useRequestSigning` follows up a create made outside the Request
 * access modal with it (Home's frequent access card): one draft opens the
 * signing page straight away; several go to My Access, where each shows
 * "Needs your signature" with its own Sign button. The modal signs its drafts
 * in place instead (`useDraftSigning`).
 *
 * The page is opened from the portal, so it shows no code to match.
 */
import { useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import { useStartRequestSignature } from "state/members/stepUp";
import {
  failureMessage,
  START_SIGNING_FAILED_MESSAGE,
} from "state/members/stepUpFailure";

import { MY_ACCESS_PATH, portalSigningState, stepUpPath } from "./navigation";

/** Opens the signing page for one draft; says why and returns false if it cannot. */
export const useSignRequest = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const start = useStartRequestSignature();

  const sign = async (requestId: string): Promise<boolean> => {
    try {
      const challenge = await start.mutateAsync(requestId);
      navigate(stepUpPath(challenge.id), {
        state: portalSigningState(MY_ACCESS_PATH),
      });
      return true;
    } catch (error) {
      toast({
        status: "error",
        variant: "subtle",
        title: "We could not start signing",
        description: failureMessage(error, START_SIGNING_FAILED_MESSAGE),
      });
      return false;
    }
  };

  return { sign, isStarting: start.isLoading };
};

/**
 * Follows up a create. Returns true when it took over (the caller should not
 * also show "Request submitted"), false when nothing needs signing.
 */
export const useRequestSigning = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { sign } = useSignRequest();

  return async (draftIds: string[]): Promise<boolean> => {
    if (draftIds.length === 0) return false;

    if (draftIds.length > 1) {
      toast({
        status: "info",
        variant: "subtle",
        title: `${draftIds.length} requests need your signature`,
        description: "Sign each one in My Access to send it for approval.",
      });
      navigate(MY_ACCESS_PATH);
      return true;
    }

    // If signing cannot start, the draft still waits in My Access.
    if (!(await sign(draftIds[0]))) navigate(MY_ACCESS_PATH);
    return true;
  };
};
